import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Scale, Mail, Lock, Eye, EyeOff, AlertTriangle, Loader2,
  Shield, Key, ArrowLeft, CheckCircle
} from 'lucide-react';
import { useAuth, usePending2FA } from '../store/authStore';

type AuthView = 'login' | '2fa' | 'recovery' | 'forgot-password' | 'reset-sent';

export function LoginPage() {
  const [view, setView] = useState<AuthView>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, verify2FA, verifyRecoveryCode, requestPasswordReset, isAuthenticated } = useAuth();
  const { isPending, pendingEmail, clear: clearPending2FA } = usePending2FA();

  // Redirect if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Check for pending 2FA
  useEffect(() => {
    if (isPending && view === 'login') {
      setView('2fa');
      setEmail(pendingEmail || '');
    }
  }, [isPending, view, pendingEmail]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await signIn(email, password);
      if (result.requires2FA) {
        setView('2fa');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const success = await verify2FA(verificationCode);
      if (!success) {
        setError('Invalid code. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRecoveryCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const success = await verifyRecoveryCode(recoveryCode);
      if (!success) {
        setError('Invalid recovery code. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await requestPasswordReset(email);
      setView('reset-sent');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    clearPending2FA();
    setView('login');
    setVerificationCode('');
    setRecoveryCode('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-primary-900 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-accent-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-accent-600/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-500 rounded-xl mb-4">
            <Scale className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-display font-bold text-white">
            Lawity
          </h1>
          <p className="text-primary-400 mt-2">
            Secure document automation for legal professionals
          </p>
        </div>

        {/* Card */}
        <div className="bg-primary-800/50 backdrop-blur-lg rounded-2xl border border-primary-700 overflow-hidden">
          <AnimatePresence mode="wait">
            {/* Login Form */}
            {view === 'login' && (
              <motion.div
                key="login"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="p-8"
              >
                <h2 className="text-xl font-semibold text-white mb-6">Sign In</h2>

                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-primary-300 mb-1.5">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-500" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@lawfirm.com"
                        required
                        autoComplete="email"
                        className="w-full pl-11 pr-4 py-3 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500 focus:ring-1 focus:ring-accent-500 outline-none placeholder-primary-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary-300 mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-500" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        autoComplete="current-password"
                        className="w-full pl-11 pr-12 py-3 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500 focus:ring-1 focus:ring-accent-500 outline-none placeholder-primary-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-500 hover:text-primary-400"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-accent-500 text-white font-medium rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <button
                    onClick={() => setView('forgot-password')}
                    className="text-sm text-accent-400 hover:text-accent-300"
                  >
                    Forgot your password?
                  </button>
                </div>

                {/* Demo credentials */}
                <div className="mt-6 p-4 bg-primary-900/50 rounded-lg">
                  <p className="text-xs text-primary-400 mb-2">Demo Credentials:</p>
                  <div className="space-y-1 text-xs font-mono">
                    <p className="text-primary-300">admin@lawity.com / demo123 (with 2FA)</p>
                    <p className="text-primary-300">demo@example.com / demo123 (no 2FA)</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* 2FA Verification */}
            {view === '2fa' && (
              <motion.div
                key="2fa"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-8"
              >
                <button
                  onClick={handleBackToLogin}
                  className="flex items-center gap-2 text-sm text-primary-400 hover:text-white mb-6"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to login
                </button>

                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-accent-500/20 rounded-xl mb-3">
                    <Shield className="w-6 h-6 text-accent-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-white">Two-Factor Authentication</h2>
                  <p className="text-sm text-primary-400 mt-2">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>

                <form onSubmit={handle2FAVerify} className="space-y-5">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => {
                      setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                      setError('');
                    }}
                    placeholder="000000"
                    autoFocus
                    className="w-full text-center text-3xl font-mono tracking-[0.5em] py-4 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500 focus:ring-1 focus:ring-accent-500 outline-none placeholder-primary-600"
                  />

                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || verificationCode.length !== 6}
                    className="w-full py-3 bg-accent-500 text-white font-medium rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? 'Verifying...' : 'Verify'}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <button
                    onClick={() => {
                      setView('recovery');
                      setError('');
                    }}
                    className="text-sm text-accent-400 hover:text-accent-300"
                  >
                    Use a recovery code instead
                  </button>
                </div>

                <p className="mt-4 text-xs text-primary-500 text-center">
                  Demo: Enter any 6-digit code (e.g., 123456)
                </p>
              </motion.div>
            )}

            {/* Recovery Code */}
            {view === 'recovery' && (
              <motion.div
                key="recovery"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-8"
              >
                <button
                  onClick={() => {
                    setView('2fa');
                    setError('');
                  }}
                  className="flex items-center gap-2 text-sm text-primary-400 hover:text-white mb-6"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to authenticator
                </button>

                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-500/20 rounded-xl mb-3">
                    <Key className="w-6 h-6 text-yellow-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-white">Recovery Code</h2>
                  <p className="text-sm text-primary-400 mt-2">
                    Enter one of your recovery codes
                  </p>
                </div>

                <form onSubmit={handleRecoveryCode} className="space-y-5">
                  <input
                    type="text"
                    value={recoveryCode}
                    onChange={(e) => {
                      setRecoveryCode(e.target.value.toUpperCase());
                      setError('');
                    }}
                    placeholder="XXXX-XXXX"
                    autoFocus
                    className="w-full text-center text-xl font-mono tracking-wider py-4 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500 focus:ring-1 focus:ring-accent-500 outline-none placeholder-primary-600"
                  />

                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-xs text-yellow-200">
                      Recovery codes can only be used once. After using this code, you'll have one less recovery code available.
                    </p>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || recoveryCode.length < 9}
                    className="w-full py-3 bg-accent-500 text-white font-medium rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? 'Verifying...' : 'Verify'}
                  </button>
                </form>

                <p className="mt-4 text-xs text-primary-500 text-center">
                  Demo: Enter any code in XXXX-XXXX format
                </p>
              </motion.div>
            )}

            {/* Forgot Password */}
            {view === 'forgot-password' && (
              <motion.div
                key="forgot"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-8"
              >
                <button
                  onClick={handleBackToLogin}
                  className="flex items-center gap-2 text-sm text-primary-400 hover:text-white mb-6"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to login
                </button>

                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-white">Reset Password</h2>
                  <p className="text-sm text-primary-400 mt-2">
                    Enter your email and we'll send you a reset link
                  </p>
                </div>

                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-primary-300 mb-1.5">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-500" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@lawfirm.com"
                        required
                        className="w-full pl-11 pr-4 py-3 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500 focus:ring-1 focus:ring-accent-500 outline-none placeholder-primary-500"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-accent-500 text-white font-medium rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              </motion.div>
            )}

            {/* Reset Email Sent */}
            {view === 'reset-sent' && (
              <motion.div
                key="reset-sent"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="p-8 text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">Check Your Email</h2>
                <p className="text-primary-400 mb-6">
                  We've sent a password reset link to<br />
                  <span className="text-white font-medium">{email}</span>
                </p>
                <button
                  onClick={handleBackToLogin}
                  className="text-accent-400 hover:text-accent-300"
                >
                  Back to login
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-primary-500 mt-6">
          © {new Date().getFullYear()} Lawity. All rights reserved.
        </p>
      </motion.div>
    </div>
  );
}

export default LoginPage;
